<template>
  <div
    ref="contentRef"
    class="tw-prose tw-prose-sm tw-max-w-none tw-break-words fast-html-content"
    v-html="modelValue"
  />
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

defineProps({
  modelValue: {
    type: String,
    required: true,
  },
})

const contentRef = ref<HTMLElement | null>(null)
const imageCleanup = new WeakMap<HTMLImageElement, () => void>()

const getResizedUrl = (url: string): string | null => {
  if (!url || !url.includes('/original/')) {
    return null
  }

  return url.replace('/original/', '/large/')
}

const syncContentImages = async () => {
  await nextTick()

  contentRef.value?.querySelectorAll<HTMLImageElement>('img').forEach((image) => {
    const originalUrl = image.dataset.originalUrl || image.getAttribute('src') || ''
    const resizedUrl = getResizedUrl(originalUrl)

    if (!resizedUrl || image.dataset.resizedUrl === resizedUrl) {
      return
    }

    image.dataset.originalUrl = originalUrl
    image.dataset.resizedUrl = resizedUrl

    const onError = () => {
      // The Lambda may still be processing the derivative. Keep the original
      // image visible until the resized object is available.
      if (image.src !== originalUrl) {
        image.src = originalUrl
      }
    }

    image.addEventListener('error', onError, { once: true })
    imageCleanup.set(image, () => image.removeEventListener('error', onError))
    image.src = resizedUrl
  })
}

const stopWatchingContent = watch(
  () => contentRef.value?.innerHTML,
  syncContentImages,
  { flush: 'post' },
)

onMounted(syncContentImages)

onBeforeUnmount(() => {
  stopWatchingContent()
  contentRef.value?.querySelectorAll<HTMLImageElement>('img').forEach((image) => {
    imageCleanup.get(image)?.()
  })
})
</script>

<style>
.fast-html-content .wft-faq-block {
  max-width: 100%;
  margin: 24px 0;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}
.fast-html-content .wft-faq-block__content {
  max-width: 100%;
  padding: 0;
}
.fast-html-content .wft-faq-block__title {
  margin: 0;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.fast-html-content .wft-faq-accordion {
  margin: 0;
  padding: 0;
}
.fast-html-content .wft-faq-accordion__item {
  margin: 0;
  border-bottom: 1px solid #eef2f7;
}
.fast-html-content .wft-faq-accordion__item:last-child {
  border-bottom: 0;
}
.fast-html-content .wft-faq-accordion__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  padding: 14px 20px;
  font-weight: 600;
  list-style: none;
  overflow-wrap: anywhere;
}
.fast-html-content .wft-faq-accordion__question::-webkit-details-marker {
  display: none;
}
.fast-html-content .wft-faq-accordion__question::after {
  content: "";
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}
.fast-html-content .wft-faq-accordion__item[open] .wft-faq-accordion__question::after {
  transform: rotate(-135deg);
}
.fast-html-content .wft-faq-accordion__answer {
  padding: 0 20px 16px;
  overflow-wrap: anywhere;
}
</style>
